import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ShoppingBag, ChevronDown } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useLanguage } from "@/context/LanguageContext";
import { PRODUCTS } from "@/lib/shop";

export default function Nav({ solid = false }) {
  const [scrolled, setScrolled] = useState(false);
  const [dropOpen, setDropOpen] = useState(false);
  const { count, openCart } = useCart();
  const { lang, setLang, t } = useLanguage();
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setDropOpen(false), [pathname]);

  const opaque = solid || scrolled;

  const SIMPLE_LINKS = [
    { to: "/made-to-order", key: "nav.mto", testid: "mto" },
    { to: "/commission", key: "nav.commissions", testid: "commissions" },
    { to: "/exhibition", key: "nav.exhibitions", testid: "exhibitions" },
    { to: "/about", key: "nav.concept", testid: "concept" },
  ];

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        opaque || dropOpen
          ? "bg-[#faf8f5]/95 backdrop-blur-md border-b border-[#111111]/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 h-14 md:h-16 flex items-center justify-between">
        <Link
          to="/"
          data-testid="nav-logo"
          className="font-display text-lg md:text-xl tracking-tight text-[#111111]"
        >
          Imaginary <span className="italic">Collection</span>
        </Link>

        <nav className="hidden md:flex items-center gap-9">
          {/* Prints dropdown — artist names */}
          <div
            className="relative"
            onMouseEnter={() => setDropOpen(true)}
            onMouseLeave={() => setDropOpen(false)}
          >
            <Link
              to="/shop"
              data-testid="nav-link-prints"
              className={`link-underline inline-flex items-center gap-1.5 text-sm tracking-wide ${
                pathname.startsWith("/shop") || pathname.startsWith("/artists")
                  ? "text-[#111111] font-normal"
                  : "text-[#111111]/75 font-light hover:text-[#111111]"
              }`}
            >
              {t("nav.prints")}
              <ChevronDown
                className={`h-3.5 w-3.5 transition-transform duration-300 ${dropOpen ? "rotate-180" : ""}`}
                strokeWidth={1.5}
              />
            </Link>

            <div
              data-testid="prints-dropdown"
              className={`absolute left-1/2 top-full -translate-x-1/2 pt-6 transition-all duration-300 ${
                dropOpen
                  ? "opacity-100 translate-y-0 pointer-events-auto"
                  : "opacity-0 -translate-y-1 pointer-events-none"
              }`}
            >
              <div className="w-[620px] border gallery-line bg-[#faf8f5] px-10 py-8 shadow-none">
                <p className="text-[10px] uppercase tracking-[0.25em] text-[#a89f8e]">
                  {t("nav.byArtist")}
                </p>
                <div className="mt-4 grid grid-cols-3 gap-x-8 gap-y-2.5">
                  {PRODUCTS.map((p) => (
                    <Link
                      key={p.id}
                      to={`/artists/${p.id}`}
                      data-testid={`nav-artist-${p.id}`}
                      className="text-sm font-light text-[#111111]/75 hover:text-[#111111] transition-colors duration-200"
                    >
                      {p.artist?.name || p.name}
                    </Link>
                  ))}
                </div>
                <div className="mt-6 border-t border-[#111111]/10 pt-4 flex items-center justify-between">
                  <Link
                    to="/artists"
                    data-testid="nav-all-artists"
                    className="link-underline text-xs uppercase tracking-[0.2em] text-[#111111]"
                  >
                    {t("artist.allArtists")}
                  </Link>
                  <Link
                    to="/shop"
                    className="link-underline text-xs uppercase tracking-[0.2em] text-[#111111]/60 hover:text-[#111111]"
                  >
                    {t("nav.allPrints")}
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {SIMPLE_LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.testid}`}
              className={`link-underline text-sm tracking-wide ${
                pathname === l.to
                  ? "text-[#111111] font-normal"
                  : "text-[#111111]/75 font-light hover:text-[#111111]"
              }`}
            >
              {t(l.key)}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-5 md:gap-6">
          {/* FR / EN switcher */}
          <div className="flex items-center text-xs tracking-[0.15em]" data-testid="lang-switcher">
            <button
              onClick={() => setLang("fr")}
              data-testid="lang-fr"
              className={`px-1 transition-colors duration-300 ${
                lang === "fr" ? "text-[#111111] font-normal" : "text-[#111111]/40 hover:text-[#111111]"
              }`}
            >
              FR
            </button>
            <span className="text-[#111111]/25">/</span>
            <button
              onClick={() => setLang("en")}
              data-testid="lang-en"
              className={`px-1 transition-colors duration-300 ${
                lang === "en" ? "text-[#111111] font-normal" : "text-[#111111]/40 hover:text-[#111111]"
              }`}
            >
              EN
            </button>
          </div>

          <Link
            to="/shop"
            className="md:hidden text-sm font-light text-[#111111]"
            data-testid="nav-shop-mobile"
          >
            {t("nav.prints")}
          </Link>
          <button
            type="button"
            onClick={openCart}
            data-testid="nav-cart"
            className="relative flex items-center text-[#111111]"
            aria-label={t("cart.title")}
          >
            <ShoppingBag className="h-5 w-5" strokeWidth={1.5} />
            {count > 0 && (
              <span
                data-testid="cart-count"
                className="absolute -top-2 -right-2.5 grid h-[18px] min-w-[18px] place-items-center rounded-full bg-[#111111] px-1 text-[10px] leading-none text-[#faf8f5]"
              >
                {count}
              </span>
            )}
          </button>
        </div>
      </div>
    </header>
  );
}
